import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Gamepad2, Wifi, Battery, Settings, Zap, Target } from 'lucide-react';

export const GameControllerSettings: React.FC = () => {
  const [controllers, setControllers] = useState([
    { id: 1, name: 'Xbox Wireless Controller', connected: true, battery: 85, type: 'xbox' },
    { id: 2, name: 'PlayStation DualSense', connected: false, battery: 0, type: 'playstation' },
    { id: 3, name: 'Pro Controller', connected: true, battery: 45, type: 'nintendo' },
  ]);

  const [vibrationEnabled, setVibrationEnabled] = useState(true);
  const [vibrationStrength, setVibrationStrength] = useState([75]);
  const [deadZone, setDeadZone] = useState([15]);
  const [sensitivity, setSensitivity] = useState([50]);
  const [autoConnect, setAutoConnect] = useState(true);
  const [lowBatteryWarning, setLowBatteryWarning] = useState(true);
  const [selectedController, setSelectedController] = useState(1);

  const getControllerIcon = (type: string) => {
    switch (type) {
      case 'xbox': return '🎮';
      case 'playstation': return '🕹️';
      case 'nintendo': return '🎯';
      default: return '🎮';
    }
  };

  const getBatteryColor = (level: number) => {
    if (level > 60) return 'text-green-500';
    if (level > 30) return 'text-yellow-500';
    return 'text-red-500';
  };

  return (
    <div className="h-full p-6 bg-background overflow-auto">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground mb-2">Game Controller Settings</h1>
          <p className="text-muted-foreground">
            Configure gaming devices and controller preferences
          </p>
        </div>

        {/* Connected Controllers */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Gamepad2 size={20} />
              Connected Controllers
            </CardTitle>
            <CardDescription>
              Manage connected gaming devices
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {controllers.map((controller) => (
              <div
                key={controller.id}
                className={`flex items-center justify-between p-4 border rounded-lg ${
                  controller.connected ? 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800' : 'bg-muted/30'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className="text-2xl">{getControllerIcon(controller.type)}</div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{controller.name}</span>
                      {controller.connected ? (
                        <Badge className="bg-green-100 text-green-700 dark:bg-green-800 dark:text-green-200">
                          Connected
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Disconnected</Badge>
                      )}
                    </div>
                    
                    {controller.connected && (
                      <div className="flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-2">
                          <Battery size={14} className={getBatteryColor(controller.battery)} />
                          <span className={getBatteryColor(controller.battery)}>
                            {controller.battery}%
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Wifi size={14} className="text-green-500" />
                          <span className="text-green-500">Strong Signal</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {controller.connected && (
                    <Progress 
                      value={controller.battery} 
                      className="w-20 h-2"
                    />
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedController(controller.id)}
                  >
                    {controller.connected ? 'Configure' : 'Connect'}
                  </Button>
                </div>
              </div>
            ))}

            <div className="pt-4">
              <Button variant="outline" className="w-full">
                <Gamepad2 size={16} className="mr-2" />
                Add New Controller
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Controller Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings size={20} />
              Controller Configuration
            </CardTitle>
            <CardDescription>
              Adjust controller behavior and responsiveness
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <Label htmlFor="auto-connect">Auto-connect controllers</Label>
              <Switch
                id="auto-connect"
                checked={autoConnect}
                onCheckedChange={setAutoConnect}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="low-battery-warning">Low battery warnings</Label>
              <Switch
                id="low-battery-warning"
                checked={lowBatteryWarning}
                onCheckedChange={setLowBatteryWarning}
              />
            </div>

            <Separator />

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="sensitivity">Stick Sensitivity</Label>
                <span className="text-sm text-muted-foreground">
                  {sensitivity[0]}%
                </span>
              </div>
              <Slider
                id="sensitivity"
                value={sensitivity}
                onValueChange={setSensitivity}
                max={100}
                step={1}
                className="w-full"
              />
              <div className="text-xs text-muted-foreground">
                Low sensitivity ← → High sensitivity
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="dead-zone">Dead Zone</Label>
                <span className="text-sm text-muted-foreground">
                  {deadZone[0]}%
                </span>
              </div>
              <Slider
                id="dead-zone"
                value={deadZone}
                onValueChange={setDeadZone}
                max={50}
                step={1}
                className="w-full"
              />
              <div className="text-xs text-muted-foreground">
                Small dead zone ← → Large dead zone
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Vibration Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap size={20} />
              Vibration & Feedback
            </CardTitle>
            <CardDescription>
              Configure haptic feedback and vibration
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <Label htmlFor="vibration-enabled">Enable vibration</Label>
              <Switch
                id="vibration-enabled"
                checked={vibrationEnabled}
                onCheckedChange={setVibrationEnabled}
              />
            </div>

            {vibrationEnabled && (
              <>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="vibration-strength">Vibration Strength</Label>
                    <span className="text-sm text-muted-foreground">
                      {vibrationStrength[0]}%
                    </span>
                  </div>
                  <Slider
                    id="vibration-strength"
                    value={vibrationStrength}
                    onValueChange={setVibrationStrength}
                    max={100}
                    step={1}
                    className="w-full"
                  />
                </div>

                <div className="pt-2">
                  <Button variant="outline" size="sm">
                    Test Vibration
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Button Mapping */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target size={20} />
              Button Mapping
            </CardTitle>
            <CardDescription>
              Customize button assignments and create profiles
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { button: 'A/Cross', action: 'Confirm' },
                { button: 'B/Circle', action: 'Cancel' },
                { button: 'X/Square', action: 'Menu' },
                { button: 'Y/Triangle', action: 'Action' },
                { button: 'LB/L1', action: 'Previous' },
                { button: 'RB/R1', action: 'Next' },
                { button: 'LT/L2', action: 'Analog' },
                { button: 'RT/R2', action: 'Analog' },
              ].map((mapping, index) => (
                <div key={index} className="p-3 bg-muted/30 rounded-lg space-y-2">
                  <div className="font-medium text-sm">{mapping.button}</div>
                  <div className="text-xs text-muted-foreground">{mapping.action}</div>
                  <Button variant="outline" size="sm" className="w-full text-xs">
                    Remap
                  </Button>
                </div>
              ))}
            </div>

            <Separator />

            <div className="space-y-4">
              <h4 className="font-medium">Controller Profiles</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Button variant="outline" className="h-16 flex flex-col items-center justify-center">
                  <div className="font-medium">Default</div>
                  <div className="text-xs text-muted-foreground">Standard mapping</div>
                </Button>
                <Button variant="outline" className="h-16 flex flex-col items-center justify-center">
                  <div className="font-medium">Gaming</div>
                  <div className="text-xs text-muted-foreground">Optimized for games</div>
                </Button>
                <Button variant="outline" className="h-16 flex flex-col items-center justify-center">
                  <div className="font-medium">Custom</div>
                  <div className="text-xs text-muted-foreground">User defined</div>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Advanced Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Advanced Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="input-lag-reduction">Input lag reduction</Label>
              <Switch id="input-lag-reduction" />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="exclusive-mode">Exclusive controller mode</Label>
              <Switch id="exclusive-mode" />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="background-input">Allow background input</Label>
              <Switch id="background-input" />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline">Reset to Defaults</Button>
          <Button>Apply Changes</Button>
        </div>
      </div>
    </div>
  );
};